import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageTitle } from "@/components/admin/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ItemCard } from "@/components/admin/smm/ItemCard";
import { PreviewDialog } from "@/components/admin/smm/PreviewDialog";
import { ScheduleDialog } from "@/components/admin/smm/ScheduleDialog";
import { SmmCalendar } from "@/components/admin/smm/SmmCalendar";
import { ITEM_STATUS_LABELS, addDays } from "@/lib/smm";

const Picks = () => {
  const qc = useQueryClient();
  const [day, setDay] = useState(null);
  const [preview, setPreview] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["smm-items", day], queryFn: () => api.get(`/admin/smm/items${day ? `?day=${day}` : ""}`).then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["smm-items"] });
  const shown = data?.day;
  const pick = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/smm/select", { day: shown, generate: true }, { timeout: 300000 });
      toast.success(`${r.added} new pick(s): ${r.deals} deals, ${r.coupons} coupons${r.content ? ` · captions written for ${r.content.generated}` : ""}`);
      if (r.content?.failed) toast.error(`${r.content.failed} caption(s) failed: ${r.content.errors[0] || ""}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const items = data?.items || [];
  const counts = items.reduce((a, i) => ({ ...a, [i.status]: (a[i.status] || 0) + 1 }), {});
  const current = preview && items.find((i) => i.id === preview.id);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input type="date" value={shown || ""} onChange={(e) => setDay(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="smm-day-input" />
        {shown && shown !== data?.today && <button onClick={() => setDay(data.today)} className="text-xs font-semibold text-emerald-700" data-testid="smm-day-today">Back to today</button>}
        <button onClick={() => setDay(addDays(shown, -1))} disabled={!shown} className="text-xs font-semibold text-slate-600" data-testid="smm-day-prev">← Previous day</button>
        <button onClick={() => setDay(addDays(shown, 1))} disabled={!shown} className="text-xs font-semibold text-slate-600" data-testid="smm-day-next">Next day →</button>
        <span className="text-xs text-slate-500" data-testid="smm-counts">{Object.entries(ITEM_STATUS_LABELS).map(([k, v]) => `${v}: ${counts[k] || 0}`).join(" · ")}</span>
        <button onClick={pick} disabled={busy} className="ml-auto inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2 rounded-lg disabled:opacity-60" data-testid="smm-pick-button">
          <Sparkles size={16} /> {busy ? "Picking & writing…" : "Pick deals & write captions"}
        </button>
      </div>
      <p className="text-xs text-slate-500">Picks run automatically every day at {data?.settings?.daily_time || "08:00"} IST (Settings → Social Media Manager). Only approved posts you schedule are published - picks themselves are never posted.</p>
      {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {!isLoading && !items.length && <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-2xl p-6" data-testid="smm-empty">No picks for this day yet. Press "Pick deals & write captions".</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="smm-items">
        {items.map((i) => <ItemCard key={i.id} item={i} platforms={data.platforms} onChanged={refresh} onPreview={setPreview} onSchedule={setSchedule} />)}
      </div>
      <PreviewDialog item={current} platformFormat={data?.platform_format || {}} onClose={() => setPreview(null)} onSaved={refresh} />
      <ScheduleDialog item={schedule} onClose={() => setSchedule(null)} onDone={() => { setSchedule(null); refresh(); qc.invalidateQueries({ queryKey: ["smm-posts"] }); }} />
    </div>
  );
};

export default function SocialManager() {
  return (
    <div data-testid="smm-page">
      <PageTitle title="Social Media Manager" sub="Daily deal picks → AI captions → your approval → calendar (IST). Approved posts go out on their own at the scheduled time to connected accounts; the rest become reminders." />
      <Tabs defaultValue="picks">
        <TabsList className="mb-4">
          <TabsTrigger value="picks" data-testid="smm-tab-picks">Today's picks</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="smm-tab-calendar">Calendar & queue</TabsTrigger>
        </TabsList>
        <TabsContent value="picks"><Picks /></TabsContent>
        <TabsContent value="calendar"><SmmCalendar /></TabsContent>
      </Tabs>
    </div>
  );
}
